import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useEditor, EditorContent } from '@tiptap/react'
import Placeholder from '@tiptap/extension-placeholder'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import FontFamily from '@tiptap/extension-font-family'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import TextAlign from '@tiptap/extension-text-align'
import Highlight from '@tiptap/extension-highlight'
import { BulletList as TiptapBulletList } from '@tiptap/extension-bullet-list'
import { OrderedList as TiptapOrderedList } from '@tiptap/extension-ordered-list'
import TextDirection from 'tiptap-text-direction'

import { ReactComponent as Bold } from '../../../assets/svgs/bold.svg'
import { ReactComponent as Italic } from '../../../assets/svgs/italic.svg'
import { ReactComponent as UnderlineIcon } from '../../../assets/svgs/underline.svg'
import { ReactComponent as Strikethrough } from '../../../assets/svgs/strikethrough.svg'
import { ReactComponent as List } from '../../../assets/svgs/list_2.svg'
import { ReactComponent as ListOrdered } from '../../../assets/svgs/list-ordered.svg'
import { ReactComponent as LinkIcon } from '../../../assets/svgs/link.svg'
import { ReactComponent as ImageIcon } from '../../../assets/svgs/image.svg'
import { ReactComponent as Undo } from '../../../assets/svgs/undo.svg'
import { ReactComponent as Redo } from '../../../assets/svgs/redo.svg'
import { ReactComponent as Type } from '../../../assets/svgs/type.svg'
import { ReactComponent as AlignLeft } from '../../../assets/svgs/align-left.svg'
import { ReactComponent as AlignCenter } from '../../../assets/svgs/align-center.svg'
import { ReactComponent as AlignRight } from '../../../assets/svgs/align-right.svg'
import { ReactComponent as AlignJustify } from '../../../assets/svgs/align-justify.svg'
import { ReactComponent as Highlighter } from '../../../assets/svgs/highlighter.svg'
import { ReactComponent as Circle } from '../../../assets/svgs/circle.svg'
import { ReactComponent as LetterCaseUpper } from '../../../assets/svgs/case-upper.svg'
import { ReactComponent as LetterCaseLower } from '../../../assets/svgs/case-lower.svg'
import { ReactComponent as Square } from '../../../assets/svgs/square.svg'
import { ReactComponent as ArrowLeftToLine } from '../../../assets/svgs/pilcrow-left.svg'
import { ReactComponent as ArrowRightToLine } from '../../../assets/svgs/pilcrow-right.svg'

import {
  EditorContainer,
  Toolbar,
  ToolbarGroup,
  ToolbarButton,
  ColorButton,
  Select,
  StyledEditorContent,
  ColorPickerContainer,
  ColorGrid,
  ColorSwatch,
  ListMenu,
  ListMenuItem,
  Tooltip,
} from './styles'

const fontFamilies = [
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Courier New', value: '"Courier New", Courier, monospace' },
  { label: 'Tahoma', value: 'Tahoma, sans-serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
]

const colors = [
  { label: 'Black', value: '#000000' },
  { label: 'Gray', value: '#6B7280' },
  { label: 'Red', value: '#EF4444' },
  { label: 'Blue', value: '#3B82F6' },
  { label: 'Green', value: '#10B981' },
]

const CustomBulletList = TiptapBulletList.extend({
  addAttributes() {
    return {
      'data-type': {
        default: 'disc',
        parseHTML: (element) => element.getAttribute('data-type') || 'disc',
        renderHTML: (attributes) => ({
          'data-type': attributes['data-type'] || 'disc',
        }),
      },
    }
  },
})

const CustomOrderedList = TiptapOrderedList.extend({
  addAttributes() {
    return {
      'data-type': {
        default: 'decimal',
        parseHTML: (element) => element.getAttribute('data-type') || 'decimal',
        renderHTML: (attributes) => ({
          'data-type': attributes['data-type'] || 'decimal',
        }),
      },
    }
  },
})

export default function RichTextEditor({
  name = undefined,
  value = '',
  onChange = () => {},
  onBlur = () => {},
  placeholder = '',
  control = undefined,
  readOnly = false,
  toolbarHidden = false,
  field = undefined,
  suggestFlag = false,
  containerProps = {},
  contentProps = {},
}) {
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [bulletMenuOpen, setBulletMenuOpen] = useState(false)
  const [orderedMenuOpen, setOrderedMenuOpen] = useState(false)
  const [currentBulletStyle, setCurrentBulletStyle] = useState('disc')
  const [currentOrderedStyle, setCurrentOrderedStyle] = useState('decimal')

  const colorPickerRef = useRef(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false,
        orderedList: false,
      }),
      Placeholder.configure({
        placeholder: ({ node }) => {
          if (readOnly || node.textContent.length > 0) {
            return null
          }
          return placeholder || 'Write something...'
        },
        emptyEditorClass: 'is-editor-empty',
      }),
      CustomBulletList,
      CustomOrderedList,
      Underline,
      TextStyle,
      Color,
      FontFamily.configure({
        types: ['textStyle'],
      }),
      Link.configure({
        openOnClick: false,
      }),
      Image,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Highlight.configure({
        multicolor: false,
      }),
      TextDirection.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: field?.value ?? value ?? '',
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      if (!readOnly && typeof onChange === 'function') {
        const html = editor.getHTML()
        onChange(html)
        field?.onChange(html)
      }
    },
  })

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return
    }

    const externalValue = field?.value ?? value ?? ''
    const currentHTML = editor.getHTML()
    let shouldUpdate = false

    if (externalValue !== currentHTML) {
      shouldUpdate = true
    }

    if (suggestFlag) {
      shouldUpdate = true
    }

    if (shouldUpdate) {
      editor.commands.setContent(externalValue, false)
      // if (suggestFlag) { editor.commands.focus('end'); } // Optional: move cursor on suggest
    }
  }, [field?.value, value, editor, suggestFlag])

  useEffect(() => {
    if (!editor || editor.isDestroyed) return

    const handleBlur = () => {
      onBlur?.()
      field?.onBlur()
    }

    editor.on('blur', handleBlur)

    return () => {
      if (!editor.isDestroyed) {
        editor.off('blur', handleBlur)
      }
    }
  }, [editor, onBlur, field])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target)
      ) {
        setShowColorPicker(false)
      }
      setBulletMenuOpen(false)
      setOrderedMenuOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const toggleBulletList = (style) => {
    if (!editor || readOnly) return
    if (editor?.isActive('bulletList')) {
      if (currentBulletStyle === style) {
        editor?.chain().focus().toggleBulletList().run()
      } else {
        editor?.commands.updateAttributes('bulletList', { 'data-type': style })
      }
    } else {
      editor?.chain().focus().toggleBulletList().run()
      editor?.commands.updateAttributes('bulletList', { 'data-type': style })
    }
    setCurrentBulletStyle(style)
    setBulletMenuOpen(false)
  }

  const toggleOrderedList = (style) => {
    if (!editor || readOnly) return
    if (editor?.isActive('orderedList')) {
      if (currentOrderedStyle === style) {
        editor?.chain().focus().toggleOrderedList().run()
      } else {
        editor?.commands.updateAttributes('orderedList', {
          'data-type': style,
        })
      }
    } else {
      editor?.chain().focus().toggleOrderedList().run()
      editor?.commands.updateAttributes('orderedList', { 'data-type': style })
    }
    setCurrentOrderedStyle(style)
    setOrderedMenuOpen(false)
  }

  const addLink = () => {
    if (!editor || readOnly) return
    const url = window.prompt('Enter URL')
    if (url && editor) {
      editor.chain().focus().setLink({ href: url }).run()
    }
  }

  const addImage = () => {
    if (!editor || readOnly) return
    const url = window.prompt('Enter image URL')
    if (url && editor) {
      editor.chain().focus().setImage({ src: url }).run()
    }
  }

  if (!editor) {
    return null
  }

  const renderToolbar = !toolbarHidden && !readOnly

  return (
    <EditorContainer
      readOnly={readOnly}
      toolbarHidden={toolbarHidden}
      {...containerProps}
    >
      {renderToolbar && (
        <Toolbar>
          <ToolbarGroup>
            <Tooltip content="Bold (Ctrl+B)">
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleBold().run()}
                disabled={
                  readOnly || !editor.can().chain().focus().toggleBold().run()
                }
                active={editor.isActive('bold')}
              >
                <Bold width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
            <Tooltip content="Italic (Ctrl+I)">
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleItalic().run()}
                disabled={
                  readOnly || !editor.can().chain().focus().toggleItalic().run()
                }
                active={editor.isActive('italic')}
              >
                <Italic width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
            <Tooltip content="Underline (Ctrl+U)">
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                disabled={
                  readOnly ||
                  !editor.can().chain().focus().toggleUnderline().run()
                }
                active={editor.isActive('underline')}
              >
                <UnderlineIcon width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
            <Tooltip content="Strikethrough">
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleStrike().run()}
                disabled={
                  readOnly || !editor.can().chain().focus().toggleStrike().run()
                }
                active={editor.isActive('strike')}
              >
                <Strikethrough width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
          </ToolbarGroup>

          <ToolbarGroup>
            <Select
              onChange={(e) =>
                editor.chain().focus().setFontFamily(e.target.value).run()
              }
              value={editor.getAttributes('textStyle').fontFamily || ''}
              disabled={readOnly}
            >
              <option
                value=""
                disabled={!editor.isActive('textStyle', { fontFamily: '' })}
              >
                Font
              </option>
              {fontFamilies.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </Select>
          </ToolbarGroup>

          <ToolbarGroup>
            <div className="relative" ref={colorPickerRef}>
              <Tooltip content="Text Color">
                <ColorButton
                  onClick={() =>
                    !readOnly && setShowColorPicker(!showColorPicker)
                  }
                  color={editor.getAttributes('textStyle').color || '#000000'}
                  active={showColorPicker}
                  disabled={readOnly}
                >
                  <Type width={16} height={16} />
                </ColorButton>
              </Tooltip>
              {showColorPicker && !readOnly && (
                <ColorPickerContainer>
                  <ColorGrid>
                    {colors.map((color) => (
                      <ColorSwatch
                        key={color.value}
                        color={color.value}
                        onClick={() => {
                          editor.chain().focus().setColor(color.value).run()
                          setShowColorPicker(false)
                        }}
                        title={color.label}
                      />
                    ))}
                  </ColorGrid>
                </ColorPickerContainer>
              )}
            </div>
            <Tooltip content="Highlight">
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleHighlight().run()}
                disabled={
                  readOnly ||
                  !editor.can().chain().focus().toggleHighlight().run()
                }
                active={editor.isActive('highlight')}
              >
                <Highlighter width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
          </ToolbarGroup>

          <ToolbarGroup>
            <Tooltip content="Align Left">
              <ToolbarButton
                onClick={() =>
                  editor.chain().focus().setTextAlign('left').run()
                }
                disabled={
                  readOnly ||
                  !editor.can().chain().focus().setTextAlign('left').run()
                }
                active={editor.isActive({ textAlign: 'left' })}
              >
                <AlignLeft width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
            <Tooltip content="Align Center">
              <ToolbarButton
                onClick={() =>
                  editor.chain().focus().setTextAlign('center').run()
                }
                disabled={
                  readOnly ||
                  !editor.can().chain().focus().setTextAlign('center').run()
                }
                active={editor.isActive({ textAlign: 'center' })}
              >
                <AlignCenter width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
            <Tooltip content="Align Right">
              <ToolbarButton
                onClick={() =>
                  editor.chain().focus().setTextAlign('right').run()
                }
                disabled={
                  readOnly ||
                  !editor.can().chain().focus().setTextAlign('right').run()
                }
                active={editor.isActive({ textAlign: 'right' })}
              >
                <AlignRight width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
            <Tooltip content="Justify">
              <ToolbarButton
                onClick={() =>
                  editor.chain().focus().setTextAlign('justify').run()
                }
                disabled={
                  readOnly ||
                  !editor.can().chain().focus().setTextAlign('justify').run()
                }
                active={editor.isActive({ textAlign: 'justify' })}
              >
                <AlignJustify width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
          </ToolbarGroup>

          <ToolbarGroup>
            <Tooltip content="Bullet List">
              <ToolbarButton
                onClick={() => !readOnly && setBulletMenuOpen(!bulletMenuOpen)}
                disabled={
                  readOnly ||
                  !editor.can().chain().focus().toggleBulletList().run()
                }
                active={editor.isActive('bulletList')}
              >
                <List width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
            <ListMenu isOpen={bulletMenuOpen && !readOnly}>
              <Tooltip content="Disc">
                <ListMenuItem
                  onClick={() => toggleBulletList('disc')}
                  active={
                    editor.isActive('bulletList') &&
                    currentBulletStyle === 'disc'
                  }
                >
                  <List width={16} height={16} />
                </ListMenuItem>
              </Tooltip>
              <Tooltip content="Circle">
                <ListMenuItem
                  onClick={() => toggleBulletList('circle')}
                  active={
                    editor.isActive('bulletList') &&
                    currentBulletStyle === 'circle'
                  }
                >
                  <Circle width={16} height={16} />
                </ListMenuItem>
              </Tooltip>
              <Tooltip content="Square">
                <ListMenuItem
                  onClick={() => toggleBulletList('square')}
                  active={
                    editor.isActive('bulletList') &&
                    currentBulletStyle === 'square'
                  }
                >
                  <Square width={16} height={16} />
                </ListMenuItem>
              </Tooltip>
            </ListMenu>

            <Tooltip content="Ordered List">
              <ToolbarButton
                onClick={() =>
                  !readOnly && setOrderedMenuOpen(!orderedMenuOpen)
                }
                disabled={
                  readOnly ||
                  !editor.can().chain().focus().toggleOrderedList().run()
                }
                active={editor.isActive('orderedList')}
              >
                <ListOrdered width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
            <ListMenu isOpen={orderedMenuOpen && !readOnly}>
              <Tooltip content="Decimal">
                <ListMenuItem
                  onClick={() => toggleOrderedList('decimal')}
                  active={
                    editor.isActive('orderedList') &&
                    currentOrderedStyle === 'decimal'
                  }
                >
                  <ListOrdered width={16} height={16} />
                </ListMenuItem>
              </Tooltip>
              <Tooltip content="Lower Alpha">
                <ListMenuItem
                  onClick={() => toggleOrderedList('lower-alpha')}
                  active={
                    editor.isActive('orderedList') &&
                    currentOrderedStyle === 'lower-alpha'
                  }
                >
                  <LetterCaseLower width={16} height={16} />
                </ListMenuItem>
              </Tooltip>
              <Tooltip content="Upper Alpha">
                <ListMenuItem
                  onClick={() => toggleOrderedList('upper-alpha')}
                  active={
                    editor.isActive('orderedList') &&
                    currentOrderedStyle === 'upper-alpha'
                  }
                >
                  <LetterCaseUpper width={16} height={16} />
                </ListMenuItem>
              </Tooltip>
              <Tooltip content="Lower Roman">
                <ListMenuItem
                  onClick={() => toggleOrderedList('lower-roman')}
                  active={
                    editor.isActive('orderedList') &&
                    currentOrderedStyle === 'lower-roman'
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 100 100"
                    width={16}
                    height={16}
                  >
                    <text
                      x="10"
                      y="80"
                      fontFamily="Arial, sans-serif"
                      fontSize="80"
                      fill="black"
                    >
                      ⅸ
                    </text>
                  </svg>
                </ListMenuItem>
              </Tooltip>
              <Tooltip content="Upper Roman">
                <ListMenuItem
                  onClick={() => toggleOrderedList('upper-roman')}
                  active={
                    editor.isActive('orderedList') &&
                    currentOrderedStyle === 'upper-roman'
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 100 100"
                    width={16}
                    height={16}
                  >
                    <text
                      x="10"
                      y="80"
                      fontFamily="Arial, sans-serif"
                      fontSize="80"
                      fill="black"
                    >
                      Ⅸ
                    </text>
                  </svg>
                </ListMenuItem>
              </Tooltip>
            </ListMenu>
          </ToolbarGroup>

          <ToolbarGroup>
            <Tooltip content="Add Link">
              <ToolbarButton
                onClick={addLink}
                disabled={
                  readOnly ||
                  !(
                    editor.can().setLink({ href: '' }) ||
                    editor.can().unsetLink()
                  )
                }
                active={editor.isActive('link')}
              >
                <LinkIcon width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
            <Tooltip content="Add Image">
              <ToolbarButton
                onClick={addImage}
                disabled={readOnly || !editor.can().setImage({ src: '' })}
              >
                <ImageIcon width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
          </ToolbarGroup>

          <ToolbarGroup>
            <Tooltip content="Left-to-Right">
              <ToolbarButton
                onClick={() =>
                  editor.chain().focus().setTextDirection('ltr').run()
                }
                disabled={readOnly || !editor.can().setTextDirection('ltr')}
                active={editor.isActive({ dir: 'ltr' })}
              >
                <ArrowLeftToLine width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
            <Tooltip content="Right-to-Left">
              <ToolbarButton
                onClick={() =>
                  editor.chain().focus().setTextDirection('rtl').run()
                }
                disabled={readOnly || !editor.can().setTextDirection('rtl')}
                active={editor.isActive({ dir: 'rtl' })}
              >
                <ArrowRightToLine width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
          </ToolbarGroup>

          <ToolbarGroup>
            <Tooltip content="Undo (Ctrl+Z)">
              <ToolbarButton
                onClick={() => editor.chain().focus().undo().run()}
                disabled={readOnly || !editor.can().undo()}
              >
                <Undo width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
            <Tooltip content="Redo (Ctrl+Y)">
              <ToolbarButton
                onClick={() => editor.chain().focus().redo().run()}
                disabled={readOnly || !editor.can().redo()}
              >
                <Redo width={16} height={16} />
              </ToolbarButton>
            </Tooltip>
          </ToolbarGroup>
        </Toolbar>
      )}
      <StyledEditorContent
        readOnly={readOnly}
        toolbarHidden={toolbarHidden}
        {...contentProps}
      >
        <EditorContent editor={editor} />
      </StyledEditorContent>
    </EditorContainer>
  )
}

RichTextEditor.propTypes = {
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  control: PropTypes.object,
  readOnly: PropTypes.bool,
  toolbarHidden: PropTypes.bool,
  field: PropTypes.shape({
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    value: PropTypes.string,
    name: PropTypes.string,
    ref: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  }),
  suggestFlag: PropTypes.bool,
  containerProps: PropTypes.object,
  contentProps: PropTypes.object,
}
